/**
 * Styling for the historical fire perimeter overlay. Shared between the web
 * renderer (useFireHistoryOverlay.web.ts) and MapLegend so the outlines on
 * the map and the swatches in the key can never drift apart, the same
 * arrangement fhszColors.ts has.
 *
 * One hue, three age bands. The hue sits outside FHSZ's yellow-orange-red
 * ramp and outside the readiness greens, so perimeters stay their own layer
 * with the hazard overlay lit and never compete with the marker grammar's
 * color channel (see docs: color means readiness, and nothing else). Age is
 * carried by stroke weight and opacity, which keeps it to one channel.
 *
 * Cutoffs are fixed years, not "N years ago": a rolling window would quietly
 * reband the map every January and make the key wrong.
 */
export type FireEra = 'recent' | 'mid' | 'old';

/** Deep plum, reading as a burn scar rather than as active fire. */
export const FIRE_HISTORY_STROKE = '#4A1942';

/**
 * Drawn under the plum stroke so it survives satellite imagery, where a dark
 * outline on dark ground disappears. Widened by CASING_EXTRA_WEIGHT.
 */
export const FIRE_HISTORY_CASING = '#FFFFFF';
export const FIRE_HISTORY_CASING_OPACITY = 0.35;
export const CASING_EXTRA_WEIGHT = 2;

interface FireEraStyle {
  weight: number;
  opacity: number;
  dashArray?: string;
}

// Big Bear holds 37 / 71 / 137 perimeters across these three bands, which is
// why the oldest is the faintest and the only dashed one: it is the largest
// group and the least certain data (CAL FIRE's own coverage notes warn that
// early records are patchy).
export const FIRE_ERA_STYLE: Record<FireEra, FireEraStyle> = {
  recent: { weight: 2.5, opacity: 0.95 },
  mid: { weight: 1.75, opacity: 0.6 },
  old: { weight: 1.25, opacity: 0.35, dashArray: '4 3' },
};

export const FIRE_ERA_ORDER: FireEra[] = ['recent', 'mid', 'old'];

export const FIRE_ERA_LABELS: Record<FireEra, string> = {
  recent: '2010 to now',
  mid: '1980 to 2009',
  old: 'Before 1980',
};

const RECENT_FROM_YEAR = 2010;
const MID_FROM_YEAR = 1980;

export function fireEra(year: number): FireEra {
  if (year >= RECENT_FROM_YEAR) return 'recent';
  if (year >= MID_FROM_YEAR) return 'mid';
  return 'old';
}

/** Source line shown in the key and in the map layers sheet. */
export const FIRE_HISTORY_SOURCE_LABEL = 'Fire perimeters 1906 to 2025, CAL FIRE FRAP';
